import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared hook for public registration forms
 * Used by both PublicRegistration and PublicLandingPage components
 */
export const usePublicForm = (campaign) => {
  const [formData, setFormData] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Handle form field changes
  const handleFieldChange = useCallback((fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Evaluate a single condition for field rules
  const evaluateCondition = useCallback((condition) => {
    const conditionField = campaign?.form_config?.fields?.find(f => f.id === condition.field_id);
    let fieldValue;
    
    if (conditionField?.type === 'name') {
      // For name fields, combine all parts to check if it's not empty
      const firstName = formData[`${condition.field_id}_first`] || '';
      const middleName = formData[`${condition.field_id}_middle`] || '';
      const lastName = formData[`${condition.field_id}_last`] || '';
      fieldValue = `${firstName}${middleName}${lastName}`.trim();
    } else {
      fieldValue = formData[condition.field_id];
    }
    
    const compareValue = condition.value;
    
    switch (condition.operator) {
      case 'equals':
        return fieldValue === compareValue;
      case 'not_equals':
        return fieldValue !== compareValue;
      case 'contains':
        return String(fieldValue || '').toLowerCase().includes(String(compareValue || '').toLowerCase());
      case 'greater_than':
        return parseFloat(fieldValue) > parseFloat(compareValue);
      case 'less_than':
        return parseFloat(fieldValue) < parseFloat(compareValue);
      case 'is_empty':
        return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || fieldValue === '';
      case 'is_not_empty':
        return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0) && fieldValue !== '';
      default:
        return false;
    }
  }, [campaign, formData]);

  // Evaluate field rules to determine field visibility and state
  const getFieldState = useCallback((fieldId) => {
    const fieldRules = campaign?.form_config?.field_rules || [];
    
    let state = {
      visible: true,
      required: null,
      disabled: false,
      value: null
    };

    fieldRules.forEach(rule => {
      const affectsField = rule.actions?.some(action => action.target_field_id === fieldId);
      if (!affectsField) return;

      const conditions = rule.conditions || [];
      let conditionsMet = false;

      if (rule.match_type === 'all') {
        conditionsMet = conditions.every(evaluateCondition);
      } else {
        conditionsMet = conditions.some(evaluateCondition);
      }

      if (conditionsMet) {
        rule.actions?.forEach(action => {
          if (action.target_field_id !== fieldId) return;

          switch (action.action) {
            case 'show':
              state.visible = true;
              break;
            case 'hide':
              state.visible = false;
              break;
            case 'require':
              state.required = true;
              break;
            case 'enable':
              state.disabled = false;
              break;
            case 'disable':
              state.disabled = true;
              break;
            case 'set_value':
              state.value = action.value;
              break;
            default:
              break;
          }
        });
      } else {
        rule.actions?.forEach(action => {
          if (action.target_field_id !== fieldId) return;
          if (action.action === 'show') {
            state.visible = false;
          }
        });
      }
    });

    return state;
  }, [campaign, evaluateCondition]);

  // Check if a field should be visible
  const isFieldVisible = useCallback((fieldId) => {
    return getFieldState(fieldId).visible;
  }, [getFieldState]);

  // Evaluate formula string with proper function handling
  const evaluateFormulaString = useCallback((formulaStr) => {
    // Replace common functions
    let result = formulaStr;
    
    result = result.replace(/sum\(([^)]+)\)/gi, (match, args) => {
      const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
      return values.reduce((a, b) => a + b, 0);
    });
    
    result = result.replace(/avg\(([^)]+)\)/gi, (match, args) => {
      const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    });
    
    result = result.replace(/min\(([^)]+)\)/gi, (match, args) => {
      const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
      return Math.min(...values);
    });
    
    result = result.replace(/max\(([^)]+)\)/gi, (match, args) => {
      const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
      return Math.max(...values);
    });
    
    result = result.replace(/round\(([^)]+)\)/gi, (match, args) => {
      return Math.round(parseFloat(args) || 0);
    });
    
    // Handle if() function: if(condition, true_value, false_value)
    let maxIterations = 10;
    while (maxIterations-- > 0 && /if\s*\(/i.test(result)) {
      result = result.replace(
        /if\s*\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
        (match, condition, trueVal, falseVal) => {
          try {
            const conditionStr = condition.trim();
            let conditionResult = false;
            
            if (conditionStr.includes('>=')) {
              const [left, right] = conditionStr.split('>=').map(s => parseFloat(s.trim()) || 0);
              conditionResult = left >= right;
            } else if (conditionStr.includes('<=')) {
              const [left, right] = conditionStr.split('<=').map(s => parseFloat(s.trim()) || 0);
              conditionResult = left <= right;
            } else if (conditionStr.includes('==')) {
              const [left, right] = conditionStr.split('==').map(s => parseFloat(s.trim()) || 0);
              conditionResult = left === right;
            } else if (conditionStr.includes('!=')) {
              const [left, right] = conditionStr.split('!=').map(s => parseFloat(s.trim()) || 0);
              conditionResult = left !== right;
            } else if (conditionStr.includes('>')) {
              const [left, right] = conditionStr.split('>').map(s => parseFloat(s.trim()) || 0);
              conditionResult = left > right;
            } else if (conditionStr.includes('<')) {
              const [left, right] = conditionStr.split('<').map(s => parseFloat(s.trim()) || 0);
              conditionResult = left < right;
            } else {
              conditionResult = parseFloat(conditionStr) !== 0;
            }
            
            return conditionResult ? (parseFloat(trueVal.trim()) || 0) : (parseFloat(falseVal.trim()) || 0);
          } catch (e) {
            console.warn('Error evaluating if() condition:', e);
            return parseFloat(falseVal.trim()) || 0;
          }
        }
      );
    }
    
    return result;
  }, []);

  // Calculate total amount based on formula or base price
  const calculateTotal = useCallback(() => {
    if (!campaign?.form_config) return 0;
    
    const formConfig = campaign.form_config;
    const fields = formConfig.fields || [];
    const masterFormula = formConfig.pricing_formula || formConfig.master_formula || '';
    
    let total = 0;
    
    if (masterFormula && masterFormula.trim()) {
      try {
        let formulaStr = masterFormula;
        
        // Replace base_price reference
        formulaStr = formulaStr.replace(/\$\{base_price\}/g, parseFloat(formConfig.base_price) || 0);
        
        // Replace field references
        fields.forEach((field) => {
          const fieldValue = formData[field.id];
          let numericValue = 0;
          
          if (field.options && fieldValue) {
            const selectedValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
            field.options.forEach((option) => {
              if (selectedValues.includes(option.value)) {
                numericValue += parseFloat(option.price_modifier) || 0;
              }
            });
          } else if (fieldValue && !isNaN(parseFloat(fieldValue))) {
            numericValue = parseFloat(fieldValue);
          }
          
          const fieldRegex = new RegExp(`\\$\\{${field.id}\\}`, 'g');
          formulaStr = formulaStr.replace(fieldRegex, numericValue);
        });
        
        // Evaluate formula functions
        formulaStr = evaluateFormulaString(formulaStr);
        
        // Safely evaluate basic math expression
        if (/^[\d\s+\-*/().]+$/.test(formulaStr)) {
          total = eval(formulaStr);
        } else {
          console.warn('Invalid formula characters detected:', formulaStr);
          total = parseFloat(formConfig.base_price) || 0;
        }
      } catch (error) {
        console.error('Error evaluating formula:', error);
        total = parseFloat(formConfig.base_price) || 0;
      }
    } else {
      total = parseFloat(formConfig.base_price) || 0;
    }

    // Apply coupon discount
    if (couponApplied) {
      if (couponApplied.discount_type === 'percentage') {
        total = total * (1 - couponApplied.discount_value / 100);
      } else {
        total = Math.max(0, total - couponApplied.discount_value);
      }
    }

    return Math.max(0, total);
  }, [campaign, formData, couponApplied, evaluateFormulaString]);

  // Validate coupon code
  const validateCoupon = useCallback(async () => {
    if (!couponCode.trim() || !campaign?.id) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/campaigns/${campaign.id}/validate-coupon`, {
        code: couponCode.trim()
      });
      
      if (response.data.valid) {
        setCouponApplied({
          code: couponCode.trim(),
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value
        });
        toast.success('Coupon applied successfully!');
      } else {
        toast.error(response.data.error || 'Invalid coupon code');
        setCouponApplied(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  }, [couponCode, campaign?.id]);

  // Remove applied coupon
  const removeCoupon = useCallback(() => {
    setCouponCode('');
    setCouponApplied(null);
    toast.info('Coupon removed');
  }, []);

  // Validate email format
  const isValidEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  // Validate phone number — 7 to 15 digits (international)
  const isValidPhone = useCallback((phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }, []);

  // Validate all required fields before submission
  const validateForm = useCallback(() => {
    const fields = campaign?.form_config?.fields || [];
    
    for (const field of fields) {
      const fieldState = getFieldState(field.id);
      if (!fieldState.visible) continue;
      
      const isRequired = fieldState.required !== null ? fieldState.required : field.required;
      
      if (isRequired) {
        if (field.type === 'name') {
          const firstName = formData[`${field.id}_first`];
          if (!firstName?.trim()) {
            return { valid: false, error: `Please fill in ${field.label || 'Name'}` };
          }
        } else if (!formData[field.id] || (Array.isArray(formData[field.id]) && formData[field.id].length === 0)) {
          return { valid: false, error: `Please fill in ${field.label || field.id}` };
        }
      }

      // Type-specific validations
      if (formData[field.id]) {
        if (field.type === 'email' && !isValidEmail(formData[field.id])) {
          return { valid: false, error: 'Please enter a valid email address' };
        }
        if ((field.type === 'phone' || field.type === 'tel') && !isValidPhone(formData[field.id])) {
          return { valid: false, error: 'Please enter a valid phone number' };
        }
      }
    }
    
    return { valid: true };
  }, [campaign, formData, getFieldState, isValidEmail, isValidPhone]);

  // Build form data for submission
  const buildSubmissionData = useCallback(() => {
    const fields = campaign?.form_config?.fields || [];
    const submissionFormData = {};
    
    fields.forEach((field) => {
      if (field.type === 'name') {
        const firstName = formData[`${field.id}_first`] || '';
        const middleName = formData[`${field.id}_middle`] || '';
        const lastName = formData[`${field.id}_last`] || '';
        submissionFormData[field.id] = {
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          full_name: `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim()
        };
      } else {
        submissionFormData[field.id] = formData[field.id];
      }
    });
    
    return submissionFormData;
  }, [campaign, formData]);

  // Get extractable fields (email, phone, name) for registration
  const getExtractedFields = useCallback(() => {
    const fields = campaign?.form_config?.fields || [];
    let email = '';
    let phone = '';
    let name = '';
    
    fields.forEach((field) => {
      if (field.type === 'email' && formData[field.id]) {
        email = formData[field.id];
      }
      if ((field.type === 'phone' || field.type === 'tel') && formData[field.id]) {
        phone = formData[field.id];
      }
      if (field.type === 'name') {
        const firstName = formData[`${field.id}_first`] || '';
        const lastName = formData[`${field.id}_last`] || '';
        name = `${firstName} ${lastName}`.trim();
      }
    });
    
    return { email, phone, name };
  }, [campaign, formData]);

  return {
    // State
    formData,
    setFormData,
    couponCode,
    setCouponCode,
    couponApplied,
    setCouponApplied,
    validatingCoupon,
    
    // Handlers
    handleFieldChange,
    validateCoupon,
    removeCoupon,
    
    // Field state helpers
    getFieldState,
    isFieldVisible,
    evaluateCondition,
    
    // Calculations
    calculateTotal,
    
    // Validation
    validateForm,
    isValidEmail,
    isValidPhone,
    
    // Data builders
    buildSubmissionData,
    getExtractedFields,
  };
};

export default usePublicForm;
